const isBrowser = () => typeof window !== 'undefined';

export const POST_LOGIN_REDIRECT_KEY = 'ppfe_post_login_redirect';

/** Reject open redirects; allow same-app relative paths only. */
export function isSafePostLoginPath(path: string): boolean {
    if (!path || typeof path !== 'string') return false;
    const trimmed = path.trim();
    if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return false;
    if (trimmed.includes('://')) return false;
    return true;
}

export function buildPostLoginPath(pathname: string, search = ''): string {
    const path = `${pathname || '/'}${search || ''}`;
    return isSafePostLoginPath(path) ? path : '/';
}

/** Current browser URL (preferred over router when persisting before SSO). */
export function getCurrentPostLoginPath(): string {
    if (!isBrowser()) return '/';
    return buildPostLoginPath(window.location.pathname, window.location.search);
}

/** Persist intended destination across SSO (sessionStorage survives localStorage.clear on 401). */
export function savePostLoginRedirect(path: string): void {
    if (!isBrowser()) return;
    if (!isSafePostLoginPath(path) || path === '/') return;
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, path);
}

/**
 * Capture the pristine deep link at app bootstrap, before BrowserRouter / effects
 * can navigate through `/` and drop the query string (e.g. Plan Index filters).
 * Feeds the same store that consumePostLoginRedirect() reads after login.
 */
export function capturePostLoginRedirect(): void {
    if (!isBrowser()) return;
    const { pathname } = window.location;
    if (pathname === '/' || pathname.includes('callback')) return;
    savePostLoginRedirect(getCurrentPostLoginPath());
}

export function getPostLoginRedirect(): string | null {
    if (!isBrowser()) return null;
    const stored = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    return stored && isSafePostLoginPath(stored) ? stored : null;
}

export function clearPostLoginRedirect(): void {
    if (!isBrowser()) return;
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
}

/** Read and remove stored redirect; falls back when absent or invalid. */
export function consumePostLoginRedirect(fallback = '/'): string {
    const stored = getPostLoginRedirect();
    clearPostLoginRedirect();
    if (stored && stored !== '/') return stored;
    return isSafePostLoginPath(fallback) ? fallback : '/';
}

export type PostLoginAppState = {
    returnTo?: string;
};

/** Resolve target after SSO without clearing storage (RBAC may still be loading). */
export function resolvePostLoginReturnPath(appState: PostLoginAppState | undefined, fallback = '/'): string {
    const fromAppState = appState?.returnTo;
    if (fromAppState && isSafePostLoginPath(fromAppState) && fromAppState !== '/') {
        savePostLoginRedirect(fromAppState);
        return fromAppState;
    }
    const stored = getPostLoginRedirect();
    if (stored && stored !== '/') return stored;
    return isSafePostLoginPath(fallback) ? fallback : '/';
}
